import React, { useState, useEffect, useCallback } from "react";
import {
  Loader,
  Message,
  Button,
  Icon,
  Popup,
  Confirm,
  Modal,
  List,
  Image,
} from "semantic-ui-react";
import { DidacticRoutesAPI } from "../../../../api";
import { RutasDidacticasEditWizard } from "./RutasDidacticasEditWizard";
import "./RutasDidacticasList.scss";

const didacticRoutesAPI = new DidacticRoutesAPI();

export function RutasDidacticasList({
  teacher,
  teacherId,
  accessToken,
  reload,
}) {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Confirm de borrado (ruta completa)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);

  // Modal de edición de la ruta
  const [showEditModal, setShowEditModal] = useState(false);
  const [routeToEdit, setRouteToEdit] = useState(null);

  // Modal de “ver escenas”
  const [showScenesModal, setShowScenesModal] = useState(false);
  const [routeScenes, setRouteScenes] = useState([]); // Escenas de la ruta
  const [routeViewing, setRouteViewing] = useState(null); // Ruta que se está visualizando

  // **Nuevo** modal para mostrar la descripción de la ruta
  const [showRouteDescriptionModal, setShowRouteDescriptionModal] =
    useState(false);

  // ================ 1) Obtener listado de rutas ================
  const fetchRoutes = useCallback(async () => {
    if (!teacherId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await didacticRoutesAPI.getRoutesByTeacher(
        accessToken,
        teacherId
      );
      setRoutes(result.routes || []);
      //console.log("Rutas didácticas:", routes);
    } catch (err) {
      console.error("Error fetching teacher routes:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, teacherId]);

  // Al montar el componente
  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Si hay un "reload" externo, volvemos a pedir rutas
  useEffect(() => {
    fetchRoutes();
  }, [reload, fetchRoutes]);

  // ================ 2) Ver escenas de la ruta (eye) ================
  const handleSeeClick = async (route) => {
    try {
      setLoading(true);
      setRouteViewing(route);

      let allScenes = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const result = await didacticRoutesAPI.getScenesByRoute(
          accessToken,
          route._id,
          currentPage,
          100
        );
        allScenes.push(...(result.scenes || []));
        totalPages = result.totalPages;
        currentPage++;
      } while (currentPage <= totalPages);

      setRouteScenes(allScenes);
      setShowScenesModal(true);
    } catch (error) {
      console.error("Error al obtener las escenas de la ruta:", error);
    } finally {
      setLoading(false);
    }
  };

  // ================ 3) Borrar ruta completa ================
  const handleDeleteClick = (route) => {
    setRouteToDelete(route);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!routeToDelete) return;
    try {
      await didacticRoutesAPI.deleteRoute(accessToken, routeToDelete._id);
      // Re-fetchear tras borrar la ruta
      fetchRoutes();
    } catch (error) {
      console.error("Error al eliminar la ruta:", error);
    } finally {
      setShowDeleteConfirm(false);
      setRouteToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setRouteToDelete(null);
  };

  // ================ 4) Editar ruta (pencil) ================
  const handleEditClick = (route) => {
    setRouteToEdit(route);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setRouteToEdit(null);
  };

  // ================ 5) Borrar escena de la ruta (trash en cada escena) ================
  const handleDeleteSceneFromRoute = async (scene) => {
    if (!routeViewing || !scene) return;
    try {
      setLoading(true);
      await didacticRoutesAPI.deleteSceneFromRoute(
        accessToken,
        routeViewing._id,
        scene._id
      );
      // Removemos la escena del array local, sin recargar
      setRouteScenes((prevScenes) =>
        prevScenes.filter((sc) => sc._id !== scene._id)
      );
    } catch (error) {
      console.error("Error al eliminar la escena de la ruta:", error);
    } finally {
      setLoading(false);
    }
  };

  // ================ Render principal ================
  if (loading) {
    return (
      <Loader active inline="centered">
        Cargando rutas didácticas...
      </Loader>
    );
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>Ocurrió un error</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  return (
    <div className="ui-materials">
      {/* Encabezado de Rutas */}
      <div className="intro-sections">
        <div className="intro-section rutas-creadas">
          <h2 className="intro-title">Mis Itinerarios</h2>
          <p className="intro-description">
            Revisa tus itinerarios, aulas y alumnos.
          </p>
          <hr className="intro-divider" />
        </div>
      </div>

      {/* Lista de Rutas o mensaje si no hay */}
      {routes.length === 0 ? (
        <Message info>
          <Message.Header>Sin Rutas</Message.Header>
          <p>Este docente no tiene rutas didácticas registradas.</p>
        </Message>
      ) : (
        <div className="rute-list">
          {routes.map((route) => (
            <div key={route._id} className="e-card-rute">
              <div className="rute-info">
                <h3 className="rute-title">{route.nameRute}</h3>
                <p className="rute-assigned">
                  {route.classnumber || "Sin clase asociada"}
                </p>
                <div className="rute-buttons">
                  <Popup
                    content="Revisar"
                    position="top center"
                    inverted
                    trigger={
                      <button
                        className="e-button-info"
                        onClick={() => handleSeeClick(route)}
                      >
                        <Icon name="eye" />
                      </button>
                    }
                  />
                  <Popup
                    content="Editar"
                    position="top center"
                    inverted
                    trigger={
                      <button
                        className="e-button-info"
                        onClick={() => handleEditClick(route)}
                      >
                        <Icon name="pencil" />
                      </button>
                    }
                  />
                  <Popup
                    content="Borrar"
                    position="top center"
                    inverted
                    trigger={
                      <button
                        className="e-button-info"
                        onClick={() => handleDeleteClick(route)}
                      >
                        <Icon name="trash" />
                      </button>
                    }
                  />
                </div>
              </div>
              <div className="rute-image">
                <img
                  src="https://repopublic-educa360.s3.eu-south-2.amazonaws.com/adminweb/virtualscenes/logoEduca/logoEducapizarra/1739643592454_Educa360panelminerva3-2191520.png"
                  alt="Rute"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm para eliminar ruta completa */}
      <Confirm
        open={showDeleteConfirm}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        content={
          routeToDelete
            ? `¿Estás seguro de que deseas eliminar la ruta "${routeToDelete.nameRute}"?`
            : "¿Estás seguro de que deseas eliminar esta ruta?"
        }
        cancelButton="No"
        confirmButton="Sí, eliminar"
      />

      {/* Modal de edición de ruta */}
      {showEditModal && routeToEdit && (
        <RutasDidacticasEditWizard
          open={showEditModal}
          teacher={teacher}
          onClose={handleCloseEditModal}
          route={routeToEdit}
          accessToken={accessToken}
          onRefresh={fetchRoutes}
        />
      )}

      {/* Modal de escenas de la ruta */}
      <Modal
        open={showScenesModal}
        onClose={() => setShowScenesModal(false)}
        size="tiny"
      >
        {/* Encabezado del modal de escenas */}
        <Modal.Header
          style={{ display: "flex", alignItems: "center", gap: "1rem" }}
        >
          {/* Botón “eye” para ver la descripción de la ruta */}
          <Button
            icon="eye"
            color="blue"
            onClick={() => setShowRouteDescriptionModal(true)}
          />
          Escenas de la Ruta: {routeViewing?.nameRute}
        </Modal.Header>

        <Modal.Content>
          {loading ? (
            <Loader active inline="centered" />
          ) : routeScenes.length === 0 ? (
            <Message info>
              Esta ruta no tiene escenas o no se han encontrado.
            </Message>
          ) : (
            <List divided relaxed>
              {routeScenes.map((sc) => (
                <List.Item key={sc._id}>
                  <Image
                    src={sc.bannertopUrl}
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      marginRight: "1rem",
                    }}
                  />

                  <List.Content>
                    <List.Header>{sc.title}</List.Header>
                    <List.Description>
                      Categoría: {sc.category.join(", ")} <br />
                      Subcategoría: {sc.subcategory.join(", ")}
                    </List.Description>
                  </List.Content>

                  {/* Botón para borrar esta escena de la ruta */}
                  <List.Content floated="right">
                    <Button
                      icon="trash"
                      color="red"
                      onClick={() => handleDeleteSceneFromRoute(sc)}
                    />
                  </List.Content>
                </List.Item>
              ))}
            </List>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowScenesModal(false)} color="grey">
            Cerrar
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Nuevo modal para la descripción de la ruta */}
      <Modal
        open={showRouteDescriptionModal}
        onClose={() => setShowRouteDescriptionModal(false)}
        size="tiny"
      >
        <Modal.Header>Descripción de la Ruta</Modal.Header>
        <Modal.Content>
          <p>{routeViewing?.description || "Sin descripción disponible."}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setShowRouteDescriptionModal(false)}
            color="grey"
          >
            Cerrar
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
